import React from 'react';
import autoBind from 'react-autobind';
import { If, Then, Else } from 'react-if';
import { CirclePicker } from 'react-color';
import Config from "../../Config.jsx";
import Localization from "../../Localization.jsx";
import ThreeDJobManager from "../../tools/ThreeDJobManager.js";
import { isUnsetCoord } from "../../utils/hotspotGeo.js";
import CreateContentDialog from "../content/CreateContentDialog.jsx";
import EditContentDialog from "../content/EditContentDialog.jsx";
import Docuement from "../../images/background/docuement.png";
import {
  ActionIcon, TextInput, Tooltip, Card, Select, Divider, Slider, Button, Accordion, Group, SimpleGrid, Text
} from '@mantine/core';
import {
  IconCurrentLocation, IconChevronDown, IconMapPin, IconPlus, IconTrash, IconNotesOff, IconArrowUp, IconArrowDown, IconSparkles
} from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../../theme/brandTheme.js";
import ListLoadingPlaceholder from "../ListLoadingPlaceholder.jsx";
import { ContentTypeIcon } from "../content/contentTypeIcon.jsx";
import {
  DndProvider, Preview, useDrag, useDrop
} from 'react-dnd-multi-backend';
import {
  HTML5Backend
} from 'react-dnd-html5-backend';
import {
  TouchBackend
} from 'react-dnd-touch-backend';
import {
  MouseTransition
} from 'react-dnd-multi-backend';
import {
  createTransition
} from 'react-dnd-multi-backend';
import {
  DragDropContext, Droppable, Draggable
} from "react-beautiful-dnd";

const TouchTransition = createTransition('touchstart', (event) => {
  return event.touches != null;
});

const getItems = count =>
  Array.from({
    length: count
  }, (v, k) => k).map(k => ({
    id: `item-${k}`,
    primary: `item ${k}`,
    secondary: k % 2 === 0 ? `Whatever for ${k}` : undefined
  }));

const getDisplayIndexValue = (row) => {
  const displayIndex = Number.parseInt(row.display_index, 10);
  return Number.isNaN(displayIndex) ? Number.MAX_SAFE_INTEGER : displayIndex;
};

const normalizeContentListDisplayIndex = (list) =>
  list.map((row, index) => ({
    ...row,
    display_index: index,
  }));

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return normalizeContentListDisplayIndex(result);
};

const getItemStyle = (isDragging, draggableStyle) => ({
  // styles we need to apply on draggables
  ...draggableStyle,

  ...(isDragging && {
    background: "rgb(245,245,245)",
    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)"
  })
});

const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? 'rgba(232, 245, 253, 0.5)' : 'transparent',
  padding: '4px',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease',
});


// Roughly the height of a component row (26px icon + 8px padding each side).
const LOADING_COMPONENT_HEIGHT = 48;
const LOADING_COMPONENT_COUNT = 3;

export const HTML5toTouch = {
  backends: [
    {
      id: 'html5',
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      id: 'touch',
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

class HotspotDetailView extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();
    this.onDragEnd = this.onDragEnd.bind(this);

    // state variables here
    this.state = {
      contentList: [],
      // Three-state list rendering: an in-flight fetch must not render as the
      // "no content in this hotspot" reminder. getContentByHotspotID() below
      // raises this before the request goes out; a detail view opened without a
      // hotspot never fetches and so is never stuck in the loading state.
      contentListLoading: false,
      hotspotName: "",
      hotspotColor: "#3f51b5",
      hotspotRadius: 10,
      hotspotVisibility: 0,
      currentContentIndex: -1,
      contentID: -1,
      contentListHeight: 0
    };
    autoBind(this);

    // getContentByHotspotID() runs from here, before mount, so
    // setContentListLoading has to know not to call setState yet.
    this.isComponentMounted = false;

    let hotspot = this.props.currentHotspot;
    if (this.props.currentHotspot) {
      this.getContentByHotspotID(hotspot['id']);
      this.state.hotspotName = hotspot['name'];
      this.state.hotspotColor = "#" + parseInt(hotspot['color']).toString(16).padStart(6, '0');
      this.state.hotspotRadius = parseFloat(hotspot['radius']);
      this.state.hotspotVisibility = parseInt(hotspot['disclose']);
    }
  }

  componentDidMount() {
    this.isComponentMounted = true;
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  // Mirrors the direct-assign + setState idiom used across this view, but stays
  // quiet before mount because the first content request is fired from the
  // constructor.
  setContentListLoading(loading) {
    this.state.contentListLoading = loading;
    if (this.isComponentMounted) {
      this.setState({ contentListLoading: loading });
    }
  }

  updateWindowDimensions() {
    let boundingRect = this.refs.visibilityDiv.getBoundingClientRect();
    let height = window.innerHeight - (boundingRect.y + boundingRect.height);
    height -= 32; // Grid spacing={2} 2*8px = 16px
    this.setState({
      contentListHeight: height,
    });
    this.state.contentListHeight = height;
  }

  getContentTypeCount() {
    // Derive from config so new types (e.g., NPC) appear without changing the legacy count
    return Config.GET_HOTSPOT_CONTENT_CONFIG(null).length - 1;
  }

  getContentByHotspotID(hotspotID) {
    let requestObj = { table: "component", filter: { hotspot_id: hotspotID } };
    this.setContentListLoading(true);
    this.props.root.refs.API.request("get_list", requestObj, this.getContentOK, this.getContentFail);
  }

  getContentOK(res) {
    this.setContentListLoading(false);
    const raw = Array.isArray(res && res.body && res.body.result) ? res.body.result : [];
    let sortedContentList = raw.slice();
    sortedContentList.sort((a, b) => {
      const displayIndexDiff = getDisplayIndexValue(a) - getDisplayIndexValue(b);
      if (displayIndexDiff !== 0) {
        return displayIndexDiff;
      }
      return Number.parseInt(a.id, 10) - Number.parseInt(b.id, 10);
    });
    sortedContentList = normalizeContentListDisplayIndex(sortedContentList);
    this.setState({ contentList: sortedContentList });
    this.state.contentList = sortedContentList;

    this.decodeContentPayload();

    // A programmatic open (map 3D-job indicator) may have arrived before the
    // list; satisfy it now that the components are known.
    if (this.pendingOpenComponentId != null) {
      const target = this.pendingOpenComponentId;
      this.pendingOpenComponentId = null;
      this.openContentById(target);
    }
  }

  // Open the edit dialog for a component by id (used by the map's 3D-job
  // indicator to jump back into a generating component). If the content list
  // has not loaded yet, remember the request and getContentOK fulfils it.
  openContentById(componentId) {
    const list = this.state.contentList || [];
    const index = list.findIndex((c) => c && parseInt(c.id) === parseInt(componentId));
    if (index === -1) {
      this.pendingOpenComponentId = componentId;
      return;
    }
    this.handleContentClicked(index);
  }

  getContentFail(err) {
    // Known-to-be-nothing rather than not-known-yet, so leave the loading state.
    this.setContentListLoading(false);
    console.log("get content from server failed");
  }

  decodeContentPayload() {
    let newContentList = this.state.contentList.slice();
    for (let i = 0; i < newContentList.length; i++) {
      try {
        newContentList[i].payload = JSON.parse(newContentList[i].payload);
      }
      catch (err) {
        console.log(err);
      }
    }
    this.setState({ contentList: newContentList });
    this.state.contentList = newContentList;
  }

  hotspotNameChanged(e) {
    this.setState({ hotspotName: e.target.value });
    this.state.hotspotName = e.target.value;
  }

  hotspotColorChanged(color, event) {
    this.setState({ hotspotColor: color.hex });
    this.state.hotspotColor = color.hex;
    this.props.parent.updateHotspotColor(color);
  }

  hotspotRadiusChanged = (event, value) => {
    this.setState({ hotspotRadius: value });
    this.state.hotspotRadius = value;
    this.props.parent.updateHotspotRadius(value);
  };

  setHotspotRadius() {
    this.props.parent.updateHotspotRadius(this.state.hotspotRadius);
  }

  hotspotVisibilityChanged(e) {
    this.setState({ hotspotVisibility: e.target.value });
    this.state.hotspotVisibility = e.target.value;
  }

  handleAddContentClicked() {
    // console.log('content list length:', this.state.contentList.length);
    // console.log('userinfo role:', this.props.root.refs.userDataProfile.state.userInfo.role);
    //check if content number is more than max
    var max_len = this.props.root.refs.userDataProfile.state.userInfo.role == 2 ? Config.MAX_COMPONENET_LEN().PREMIUM : Config.MAX_COMPONENET_LEN().NORMAL;
    // console.log('max len:', max_len);
    if (this.state.contentList.length >= max_len) {
      // pop up error message
      this.props.root.addNotification(Localization.strings.EXCEED_MAX_COMPONENT, false);
    } else {
      this.setState({ currentContentIndex: -1 });
      this.state.currentContentIndex = -1;
      this.refs.createContentDialog.changeActiveStep(0);
      this.refs.createContentDialog.setState({ open: true });
    }
  }

  handleContentClicked(index) {
    this.setState({ currentContentIndex: index });
    this.state.currentContentIndex = index;
    this.refs.editContentDialog.setState({ open: true });
  }

  // Whether the current user may use the "AI Suggestions" feature. Mirrors the
  // same check in HotspotViewerBar so the trigger button can be disabled at render
  // time without depending on the sibling component's ref being attached yet.
  hasAiSuggestionPrivilege() {
    if (!this.props.root || typeof this.props.root.getPrivilege !== 'function') {
      return true;
    }
    const privilege = this.props.root.getPrivilege('AI_GENERATE_CONTENT_SUGGESTIONS');
    if (!privilege || privilege.value == null) {
      return false;
    }
    const valueStr = `${privilege.value}`.trim().toLowerCase();
    if (valueStr === '1' || valueStr === 'true' || valueStr === 'yes') {
      return true;
    }
    const asNumber = Number(privilege.value);
    return !Number.isNaN(asNumber) && asNumber > 0;
  }

  // Opens the AI suggestion dialog, which lives in the sibling HotspotViewerBar.
  handleOpenAiDialog() {
    const bar = (this.props.parent && this.props.parent.refs)
      ? this.props.parent.refs.hotspotViewerBar : null;
    const openDialog = () => {
      if (bar && typeof bar.handleShowAiResponseDialog === 'function') {
        bar.handleShowAiResponseDialog();
      }
    };

    // AI Suggestions feeds the hotspot name to the model as the main prompt, so a
    // hotspot still carrying its default name — or no name at all — yields generic,
    // unhelpful suggestions. Nudge the user to rename it first, but let them proceed
    // anyway. Match every known default across languages ("New Hotspot" / "新熱點" /
    // "新热点") since a hotspot may have been created in a different language / client.
    const name = (this.state.hotspotName || '').trim();
    const defaultNames = ['新熱點', '新热点', 'New Hotspot'];
    const isDefaultName = name === '' ||
      defaultNames.some((d) => d.toLowerCase() === name.toLowerCase());

    const confirmDialog = (this.props.root && this.props.root.refs)
      ? this.props.root.refs.confirmDialog : null;

    if (isDefaultName && confirmDialog && typeof confirmDialog.show === 'function') {
      const shownName = name === '' ? (Localization.strings.NEW_HOTSPOT || 'New Hotspot') : name;
      confirmDialog.show(
        {
          title: Localization.strings.AI_SUGGEST_RENAME_TITLE,
          content: Localization.strings.AI_SUGGEST_RENAME_REMINDER.replace('{0}', shownName),
          confirm: Localization.strings.CONTINUE_ANYWAY,
          cancel: Localization.strings.CANCEL,
        },
        openDialog,   // "Continue Anyway" -> open the AI dialog with the default name
        () => {},     // "Cancel" -> dismiss so the user can rename the hotspot first
      );
      return;
    }

    openDialog();
  }

  refreshContentList() {
    this.getContentByHotspotID(this.props.currentHotspot.id);
  }

  updateContentDisplayIndexOK(res) {
    console.log('OK:', res);
  }

  updateContentDisplayIndexFail(err) {
    console.log('update content display index failed');
  }

  /**
   * POST /api/component/reorder is POSITIONAL: the submitted array IS the
   * order (the server writes display_index 0..n-1), and the submitted set must
   * equal the hotspot's set EXACTLY — a partial list would renumber a subset
   * from 0 and collide with the rows it left alone. So this sends every id in
   * `contentList` order rather than the old (component_id, component_dIndex)
   * pairs, and names the hotspot it is reordering.
   */
  updateContentDisplayIndex(contentList = this.state.contentList) {
    const list = Array.isArray(contentList) ? contentList : [];
    let requestObj = {
      hotspot_id: this.props.currentHotspot.id,
      component_ids: list.map((content) => content.id),
    };
    this.props.root.refs.API.request("set_components_display_index", requestObj, this.updateContentDisplayIndexOK, this.updateContentDisplayIndexFail);
  }

  /**
   * Move-up / move-down is an array splice on the client now (the swap shape no
   * longer exists on the server) — the whole reordered list is submitted.
   */
  moveContent(fromIndex, toIndex) {
    const list = Array.isArray(this.state.contentList) ? this.state.contentList.slice() : [];
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) {
      return;
    }
    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);

    let requestObj = {
      hotspot_id: this.props.currentHotspot.id,
      component_ids: list.map((content) => content.id),
    };
    this.props.root.refs.API.request("swap_components_display_index", requestObj, this.swapContentDisplayIndexOK, this.swapContentDisplayIndexFail);
  }

  swapContentDisplayIndexOK(res) {
    this.refreshContentList();
  }

  swapContentDisplayIndexFail(err) {
    console.log("move content fail " + err);
  }

  moveUpContentClicked(index) {
    this.moveContent(index, index - 1);
  }

  moveDownContentClicked(index) {
    this.moveContent(index, index + 1);
  }

  deleteContentClicked(id) {
    this.setState({ contentID: id });
    this.state.contentID = id;
    let dialogText = {
      title: Localization.strings.DELETE_CONTENT,
      content: Localization.strings.DELETE_REMINDER,
      confirm: Localization.strings.DELETE,
    };
    this.props.root.refs.confirmDialog.show(dialogText, this.deleteContentConfirmClicked, this.deleteContentCancelClicked);
  }

  deleteContentConfirmClicked() {
    if (this.state.contentID >= 0) {
      // Tear down any 3D generation first (remote task cancel + cron row +
      // background registry) — the bookkeeping writes authorize against the
      // component, so they cannot land after the row is gone.
      ThreeDJobManager.handleComponentDeleted(this.state.contentID);
      let requestObj = { table: "component", filter: { id: this.state.contentID } };
      this.props.root.refs.API.request("delete", requestObj, this.deleteContentOK, this.deleteContentFail);
    }
    this.setState({ contentID: -1 });
    this.state.contentID = -1;
  }

  deleteContentCancelClicked() {
    this.setState({ contentID: -1 });
    this.state.contentID = -1;
  }

  deleteContentOK(res) {
    this.refreshContentList();

    var requestObj = { epack_id: this.props.root.getCurrentLocale().id };
    this.props.root.refs.API.request("touch_epack", requestObj);
  }

  deleteContentFail(err) {
    console.log("delete content fail " + err);
  }

  getCurrentContent() {
    if (this.state.currentContentIndex < 0) {
      return null;
    }
    else {
      return this.state.contentList[this.state.currentContentIndex];
    }
  }

  onDragEnd(result) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    if (result.source.index === result.destination.index) {
      return;
    }

    const contentList = reorder(
      this.state.contentList,
      result.source.index,
      result.destination.index
    );

    this.setState({
      contentList
    }, () => {
      this.updateContentDisplayIndex(contentList);
    });
  }

  render() {
    let unpositioned = isUnsetCoord(this.props.currentHotspot['lat']) && isUnsetCoord(this.props.currentHotspot["lon"]);
    // Three states, not two: until the request answers, "this hotspot has no
    // content" is a claim we cannot make yet.
    const contentListLoading = this.state.contentListLoading && this.state.contentList.length == 0;

    return (
      <div className="margin-std padding-std">
        <CreateContentDialog root={this.props.root} parent={this} ref="createContentDialog" />
        <EditContentDialog root={this.props.root} parent={this} ref="editContentDialog" />

        <Card withBorder>
          <SimpleGrid cols={{ base: 1, sm: 12 }} spacing="md" verticalSpacing="md" style={{ alignItems: 'center' }}>
            <div style={{ gridColumn: 'span 3' }}>
              {Localization.strings.NAME}
            </div>
            <div style={{ gridColumn: 'span 9' }}>
              <TextInput className="width-full"
                size="sm"
                value={this.state.hotspotName}
                onChange={this.hotspotNameChanged}
                maxLength={Config.MAX_TEXT_LEN().NAME}
              />
            </div>

            <div style={{ gridColumn: 'span 3' }}>
              {Localization.strings.POSITION}
            </div>
            <div style={{ gridColumn: 'span 9' }}>
              <If condition={unpositioned}>
                <Then>
                  <Button
                    variant="default"
                    leftSection={<IconCurrentLocation size={18} />}
                    onClick={this.props.parent.setHotspotPosition}
                  >
                    {Localization.strings.SET_POSITION}
                  </Button>
                </Then>
                <Else>
                  <TextInput className="width-full text-sm"
                    size="sm"
                    label={Localization.strings.LATITUDE}
                    value={this.props.currentHotspot.lat}
                    disabled
                    readOnly
                  />
                  <TextInput className="width-full text-sm"
                    size="sm"
                    label={Localization.strings.LONGITUDE}
                    value={this.props.currentHotspot.lon}
                    disabled
                    readOnly
                  />
                </Else>
              </If>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <Divider />
            </div>

            <div style={{ gridColumn: 'span 3' }}>
              {Localization.strings.COLOR}
            </div>
            <div style={{ gridColumn: 'span 9' }}>
              <Accordion disabled={unpositioned} variant="contained" className="padding-sm margin-zero" chevron={<IconChevronDown size={18} />}>
                <Accordion.Item value="color">
                  <Accordion.Control>
                    <IconMapPin size={20} style={{ color: this.state.hotspotColor }} />
                  </Accordion.Control>
                  <Accordion.Panel>
                    <CirclePicker
                      color={this.state.hotspotColor}
                      width="100%"
                      onChange={this.hotspotColorChanged}
                      circleSize={20}
                      circleSpacing={10}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </div>

            <div style={{ gridColumn: 'span 3' }}>
              {Localization.strings.RADIUS}
            </div>

            <div style={{ gridColumn: 'span 7' }}>
              <Slider
                disabled={unpositioned}
                value={this.state.hotspotRadius}
                onChange={(value) => this.hotspotRadiusChanged(null, value)}
                onChangeEnd={this.setHotspotRadius}
                min={10}
                max={100}
                step={5}
                style={{ margin: 'auto' }}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              {this.state.hotspotRadius} {Localization.strings.METER}
            </div>

            {/* disable hidden feature */}
            <div style={{ display: 'none', gridColumn: '1 / -1' }}>
              <div>
                {Localization.strings.HOTSPOT_VISIBILITY}
              </div>
              <div>
                <Select
                  disabled={unpositioned}
                  value={String(this.state.hotspotVisibility)}
                  onChange={(v) => this.hotspotVisibilityChanged({ target: { value: Number(v) } })}
                  allowDeselect={false}
                  data={[
                    { value: "1", label: Localization.strings.HOTSPOT_VISIBLE },
                    { value: "0", label: Localization.strings.HOTSPOT_INVISIBLE },
                  ]}
                />
              </div>
            </div>



            <div style={{ gridColumn: '1 / -1' }}>
              <Divider />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <Group justify="center" gap="sm">
                <Button
                  className="margin-sm"
                  leftSection={<IconPlus size={18} />}
                  onClick={this.props.parent.addContentClicked}
                >
                  {Localization.strings.ADD_CONTENT}
                </Button>
                <Tooltip label={this.hasAiSuggestionPrivilege()
                  ? Localization.strings.AI_SUGGEST_BUTTON
                  : Localization.strings.UPGRADE_TO_PREMIUM}>
                  <span>
                    <Button
                      className="margin-sm"
                      variant="light"
                      leftSection={<IconSparkles size={18} />}
                      onClick={this.handleOpenAiDialog}
                      disabled={!this.hasAiSuggestionPrivilege()}
                    >
                      {Localization.strings.AI_SUGGEST_BUTTON}
                    </Button>
                  </span>
                </Tooltip>
              </Group>
            </div>

            <div ref="visibilityDiv" style={{ gridColumn: '1 / -1', padding: '0' }}>
              <div style={{ overflow: "auto", padding: "0 0 8px" }}>
                <If condition={contentListLoading}>
                  <Then>
                    <ListLoadingPlaceholder
                      count={LOADING_COMPONENT_COUNT}
                      height={LOADING_COMPONENT_HEIGHT}
                      gap={16}
                      style={{ margin: '8px 0' }}
                    />
                  </Then>
                  <Else>
                    <If condition={this.state.contentList.length > 0}>
                      <Then>
                        <DndProvider options={HTML5toTouch}>
                          <DragDropContext onDragEnd={this.onDragEnd}>
                            <Droppable droppableId="droppable">
                              {(provided, snapshot) => (
                                <div style={getListStyle(snapshot.isDraggingOver)} ref={provided.innerRef}>
                                  {this.state.contentList.map((row, i) => (
                                    <Draggable key={row.id} draggableId={String(row.id)} index={i}>
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          onClick={() => this.handleContentClicked(i)}
                                          style={{
                                            ...getItemStyle(
                                              snapshot.isDragging,
                                              provided.draggableProps.style,
                                            ),
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            margin: '8px 0',
                                            padding: '8px 12px',
                                            border: '1px solid #eeeeee',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          <div style={{ minWidth: '40px', display: 'flex', alignItems: 'center' }}>
                                            <ContentTypeIcon
                                              ligature={Number(row['kind']) === 14
                                                ? Config.GET_HOTSPOT_CONTENT_CONFIG(Config.HOTSPOT_CONTENT_NPC).icon
                                                : Config.GET_HOTSPOT_CONTENT_CONFIG(row['kind']).icon}
                                              size={26}
                                              color="#666"
                                            />
                                          </div>
                                          <div style={{ flex: 1, minWidth: 0, paddingRight: '8px', overflow: 'hidden' }}>
                                            <span style={{
                                              fontSize: '1.0rem',
                                              fontWeight: '500',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              display: 'block'
                                            }}>
                                              {row['name'] == ""
                                                ? (Number(row['kind']) === 14
                                                    ? Config.GET_HOTSPOT_CONTENT_CONFIG(Config.HOTSPOT_CONTENT_NPC).title
                                                    : Config.GET_HOTSPOT_CONTENT_CONFIG(row['kind']).title)
                                                : row['name']}
                                            </span>
                                          </div>
                                          <Group gap={4} style={{ marginLeft: 'auto', marginRight: '8px' }}>
                                            {/* <If condition={i > 0}>
                                              <Then>
                                                <Tooltip label={Localization.strings.MOVE_UP}>
                                                  <ActionIcon
                                                    variant="subtle"
                                                    onClick={() => this.moveUpContentClicked(i)}
                                                    size="sm"
                                                  >
                                                    <IconArrowUp size={18} style={{ color: snapshot.isDragging ? "transparent" : "#bdbdbd" }} />
                                                  </ActionIcon>
                                                </Tooltip>
                                              </Then>
                                            </If>
                                            <If condition={i < this.state.contentList.length - 1}>
                                              <Then>
                                                <Tooltip label={Localization.strings.MOVE_DOWN}>
                                                  <ActionIcon
                                                    variant="subtle"
                                                    onClick={() => this.moveDownContentClicked(i)}
                                                    size="sm"
                                                  >
                                                    <IconArrowDown size={18} style={{ color: snapshot.isDragging ? "transparent" : "#bdbdbd" }} />
                                                  </ActionIcon>
                                                </Tooltip>
                                              </Then>
                                            </If> */}
                                            <Tooltip label={Localization.strings.DELETE}>
                                              <ActionIcon
                                                variant="subtle"
                                                color="gray"
                                                onClick={(e) => { e.stopPropagation(); this.deleteContentClicked(row['id']); }}
                                                size="sm"
                                              >
                                                <IconTrash size={18} style={{ color: snapshot.isDragging ? "transparent" : "#bdbdbd" }} />
                                              </ActionIcon>
                                            </Tooltip>
                                          </Group>
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </DndProvider>
                      </Then>

                      <Else>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '24px 12px',
                          padding: '28px 24px',
                          backgroundColor: 'white',
                          border: '1px solid #eeeeee',
                          borderRadius: '8px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                          <IconNotesOff
                            size={64}
                            style={{
                              color: '#9e9e9e',
                              marginBottom: '16px',
                              opacity: 0.7
                            }}
                          />
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#616161',
                            textAlign: 'center',
                            maxWidth: '450px',
                            lineHeight: '1.5',
                            fontWeight: '400'
                          }}>
                            {Localization.strings.EMPTY_CONTENT_REMINDER}
                          </div>
                        </div>
                      </Else>
                    </If>
                  </Else>
                </If>
              </div>
            </div>
          </SimpleGrid>
        </Card>


      </div>
    );
  }
}

export default HotspotDetailView;
